import { extractLastNameComponent } from '@spotify/confidence-core-react';
import { ActivityFragment } from '@spotify/confidence-graphql';
import {
  TASK_ACTIVITY_TYPES,
  TaskState,
} from '@spotify/plugin-confidence-issues-react';

const DISPLAY_NAME_FIELD_PATHS = ['/displayName', 'displayName'];
const TASK_START_COLLAPSE_WINDOW_MS = 10 * 1000;
const TASK_COMPLETION_COLLAPSE_WINDOW_MS = 30 * 1000;

const getChangeStringValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

export const getTaskLabelFromActivity = (
  activity: ActivityFragment,
): string => {
  const changes = activity.changeSet?.changes ?? [];

  for (const change of changes) {
    if (!DISPLAY_NAME_FIELD_PATHS.includes(change.fieldPath ?? '')) {
      continue;
    }

    const displayName =
      getChangeStringValue(change.newValue) ??
      getChangeStringValue(change.oldValue);

    if (displayName) {
      return displayName;
    }
  }

  return extractLastNameComponent(activity.primaryResource) ?? 'Task';
};

export type ActivityListEntry = {
  activity: ActivityFragment;
  collapsedTaskLifecycle?: 'created-started' | 'created-started-completed';
};

const isTaskCreatedActivity = (activity: ActivityFragment): boolean =>
  activity.name.startsWith(`activityTypes/${TASK_ACTIVITY_TYPES.created}/`);

const isTaskStateChangeActivity = (
  activity: ActivityFragment,
  state: TaskState,
): boolean =>
  activity.name.startsWith(
    `activityTypes/${TASK_ACTIVITY_TYPES.stateChanged}/`,
  ) &&
  activity.changeSet?.changes.some(
    change => change.fieldPath === '/state' && change.newValue === state,
  ) === true;

const isSameTaskActivityWithinWindow = ({
  laterActivity,
  earlierActivity,
  windowMs,
}: {
  laterActivity: ActivityFragment;
  earlierActivity: ActivityFragment;
  windowMs: number;
}): boolean =>
  laterActivity.primaryResource === earlierActivity.primaryResource &&
  Date.parse(laterActivity.activityTime) -
    Date.parse(earlierActivity.activityTime) <=
    windowMs;

const areCreatedAndStartedActivities = (
  taskStartedActivity: ActivityFragment,
  taskCreatedActivity: ActivityFragment,
): boolean =>
  isTaskStateChangeActivity(taskStartedActivity, TaskState.InProgress) &&
  isTaskCreatedActivity(taskCreatedActivity) &&
  isSameTaskActivityWithinWindow({
    laterActivity: taskStartedActivity,
    earlierActivity: taskCreatedActivity,
    windowMs: TASK_START_COLLAPSE_WINDOW_MS,
  });

export const collapseTaskCreatedAndStartedActivities = (
  activities: ActivityFragment[],
): ActivityListEntry[] => {
  const entries: ActivityListEntry[] = [];

  for (
    let activityIndex = 0;
    activityIndex < activities.length;
    activityIndex += 1
  ) {
    const activity = activities[activityIndex];
    const nextActivity = activities[activityIndex + 1];
    const followingActivity = activities[activityIndex + 2];

    if (
      nextActivity &&
      followingActivity &&
      isTaskStateChangeActivity(activity, TaskState.Completed) &&
      areCreatedAndStartedActivities(nextActivity, followingActivity) &&
      isSameTaskActivityWithinWindow({
        laterActivity: activity,
        earlierActivity: followingActivity,
        windowMs: TASK_COMPLETION_COLLAPSE_WINDOW_MS,
      })
    ) {
      entries.push({
        activity,
        collapsedTaskLifecycle: 'created-started-completed',
      });
      activityIndex += 2;
      continue;
    }

    if (
      nextActivity &&
      areCreatedAndStartedActivities(activity, nextActivity)
    ) {
      entries.push({
        activity,
        collapsedTaskLifecycle: 'created-started',
      });
      activityIndex += 1;
      continue;
    }

    entries.push({ activity });
  }

  return entries;
};
